import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api";
import toastr from "toastr";

interface PatientInfo {
  id: number;
  P_name: string;
  prefix: string;
  P_code: string;
  mobile: string;
  gender: string;
  age: number;
  age_type: string;
}

// `source` tells us which table this bill actually came from — `billing`
// (bills created going forward, via saveEntry) or `patient_entry`
// (historical/imported lab-test bills that never got a `billing` row).
// billing.id and patient_entry.id are two independent, overlapping id
// spaces, so `source` is required to know which table `id` refers to.
type BillSource = "billing" | "patient_entry";

interface Bill {
  id: number;
  source: BillSource;
  billnum: string;
  bill_date: string;
  bill_description: string;
  patient_name: string;
  ref_prefix: string;
  reference: string;
  total: number;
  net_amount: number;
  home_visit: number;
  payment_method: string;
  reference_no: string;
  bill_status: number;
  entry_id?: number | null;
  test_status?: number | null;
}

interface Receipt {
  id: number;
  collected_date: string;
  amount: number;
  payment_method: string;
  reference_no: string;
  bill_id: number;
  // "billing" | "patient_entry" — which table bill_id refers to, mirrors
  // Bill.source. Needed for the same reason: bill_id spaces overlap
  // across the two source tables, so matching a receipt to a bill must
  // compare both bill_id AND bill_type, not bill_id alone.
  bill_type: BillSource;
  billnum: string;
  description: string;
  type: string;
}

const PatientLog: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<"bills" | "receipts">("bills");
  const [patientInfo, setPatientInfo] = useState<PatientInfo | null>(null);
  const [bills, setBills] = useState<Bill[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) fetchAll();
  }, [id]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [patRes, billRes, receiptRes] = await Promise.all([
        API.get(`/patients/${id}`),
        API.get(`/patients/${id}/bills`),
        API.get(`/patients/${id}/receipts`),
      ]);
      setPatientInfo(patRes.data?.data || null);
      setBills(Array.isArray(billRes.data?.data) ? billRes.data.data : []);
      setReceipts(Array.isArray(receiptRes.data?.data) ? receiptRes.data.data : []);
    } catch (err) {
      console.error("fetchAll error:", err);
      toastr.error("Failed to load patient data");
    } finally {
      setLoading(false);
    }
  };

  // Match a receipt to its bill by BOTH bill_id and source/bill_type —
  // matching on bill_id alone would be wrong whenever a billing.id and a
  // patient_entry.id happen to share the same number (they're separate,
  // overlapping id spaces).
  const getPaidAmount = (bill: Bill): number =>
    receipts
      .filter((r) => r.bill_id === bill.id && r.bill_type === bill.source)
      .reduce((sum, r) => sum + Number(r.amount || 0), 0);

  const getBalanceAmount = (bill: Bill): number =>
    Number(bill.net_amount || 0) - getPaidAmount(bill);

  const getStatusBadge = (bill: Bill) => {
    const balance = getBalanceAmount(bill);
    if (bill.bill_status === 1 || balance <= 0)
      return <span className="badge bg-success">Paid</span>;
    const paid = getPaidAmount(bill);
    if (paid > 0 && balance > 0)
      return <span className="badge bg-warning text-dark">Partial</span>;
    return <span className="badge bg-danger">Unpaid</span>;
  };

  // Use navigate() — opens inside the same app, no new tab.
  // `source` is passed through as a query param so the receipt endpoint
  // can query the correct table directly instead of guessing between
  // `billing` and `patient_entry` (which can have colliding ids).
  const goToPosReceipt = (billId: number, source: BillSource) =>
    navigate(`/lab/posreciept/${billId}?source=${source}`);
  const goToA4Receipt = (billId: number, source: BillSource) =>
    navigate(`/lab/BillPdf/${billId}?source=${source}`);

  // View report — opens Test Receipt page like in Completed Reports
  const goToReport = (bill: Bill) => {
    const reportId = bill.entry_id ?? (bill.source === "patient_entry" ? bill.id : null);
    if (!reportId) {
      toastr.warning("No lab test entry found for this bill.");
      return;
    }
    if (bill.test_status === 0) {
      toastr.info("Note: Test results are still pending for this bill.");
    }
    navigate(`/report/test_receipt/${reportId}?header=0`);
  };

  const goToReportFromReceipt = (r: Receipt) => {
    const matchedBill = bills.find(
      (b) => b.id === r.bill_id && b.source === r.bill_type
    );
    const reportId =
      matchedBill?.entry_id ??
      (r.bill_type === "patient_entry" ? r.bill_id : null) ??
      r.bill_id;

    if (!reportId) {
      toastr.warning("No lab test entry found for this receipt.");
      return;
    }
    if (matchedBill && matchedBill.test_status === 0) {
      toastr.info("Note: Test results are still pending for this bill.");
    }
    navigate(`/report/test_receipt/${reportId}?header=0`);
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">Patient History</h4>
          {patientInfo && (
            <small className="text-muted">
              {patientInfo.prefix}. {patientInfo.P_name}&nbsp;|&nbsp;
              {patientInfo.P_code}&nbsp;|&nbsp;
              {patientInfo.mobile}
            </small>
          )}
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-light"
            onClick={() => navigate(`/patientfilter/${id}`)}
          >
            <i className="fa fa-plus me-1" /> New Entry
          </button>
          <button className="btn btn-light" onClick={() => navigate("/patients")}>
            <i className="fa fa-arrow-left me-1" /> Back
          </button>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" />
                </div>
              ) : (
                <>
                  {/* Tabs */}
                  <ul className="nav nav-tabs nav-justified mb-3">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "bills" ? "active" : ""}`}
                        onClick={() => setActiveTab("bills")}
                      >
                        <i className="fa fa-file-text-o me-1" />
                        Patient Log
                        {bills.length > 0 && (
                          <span className="badge bg-secondary ms-2">{bills.length}</span>
                        )}
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "receipts" ? "active" : ""}`}
                        onClick={() => setActiveTab("receipts")}
                      >
                        <i className="fa fa-credit-card me-1" />
                        Payment Receipt
                        {receipts.length > 0 && (
                          <span className="badge bg-secondary ms-2">{receipts.length}</span>
                        )}
                      </button>
                    </li>
                  </ul>

                  {/* Patient Log Tab */}
                  {activeTab === "bills" && (
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered">
                        <thead>
                          <tr style={{ backgroundColor: "#81888c", color: "white" }}>
                            <th>#</th>
                            <th>Date</th>
                            <th>Bill No.</th>
                            <th className="text-end">Bill Amount</th>
                            <th className="text-end">Paid Amount</th>
                            <th className="text-end">Balance Amount</th>
                            <th className="text-center">Status</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bills.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="text-center text-muted py-4">
                                No bills found for this patient.
                              </td>
                            </tr>
                          ) : (
                            bills.map((bill, idx) => {
                              const paid    = getPaidAmount(bill);
                              const balance = getBalanceAmount(bill);
                              return (
                                <tr key={`${bill.source}-${bill.id}`}>
                                  <td>{idx + 1}</td>
                                  <td>{bill.bill_date}</td>
                                  <td><strong>{bill.billnum}</strong></td>
                                  <td className="text-end">
                                    ₹{Number(bill.net_amount || 0).toFixed(2)}
                                  </td>
                                  <td className="text-end text-success">
                                    ₹{paid.toFixed(2)}
                                  </td>
                                  <td
                                    className="text-end"
                                    style={{ color: balance > 0 ? "red" : "green" }}
                                  >
                                    ₹{balance.toFixed(2)}
                                  </td>
                                  <td className="text-center">{getStatusBadge(bill)}</td>
                                  <td className="text-center">
                                    <div className="btn-group btn-group-sm">
                                      <button
                                        className="btn btn-light"
                                        title="View Report"
                                        onClick={() => goToReport(bill)}
                                      >
                                        <i className="fa fa-file-medical text-primary" />
                                      </button>
                                      <button
                                        className="btn btn-light"
                                        title="View A4 Receipt"
                                        onClick={() => goToA4Receipt(bill.id, bill.source)}
                                      >
                                        <i className="fa fa-file-alt" />
                                      </button>
                                      <button
                                        className="btn btn-light"
                                        title="View POS Receipt"
                                        onClick={() => goToPosReceipt(bill.id, bill.source)}
                                      >
                                        <i className="fa fa-print" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                        {bills.length > 0 && (
                          <tfoot>
                            <tr className="table-secondary fw-bold">
                              <td colSpan={3} className="text-end">Total</td>
                              <td className="text-end">
                                ₹{bills.reduce((s, b) => s + Number(b.net_amount || 0), 0).toFixed(2)}
                              </td>
                              <td className="text-end text-success">
                                ₹{bills.reduce((s, b) => s + getPaidAmount(b), 0).toFixed(2)}
                              </td>
                              <td
                                className="text-end"
                                style={{
                                  color:
                                    bills.reduce((s, b) => s + getBalanceAmount(b), 0) > 0
                                      ? "red" : "green",
                                }}
                              >
                                ₹{bills.reduce((s, b) => s + getBalanceAmount(b), 0).toFixed(2)}
                              </td>
                              <td colSpan={2} />
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  )}

                  {/* Payment Receipt Tab */}
                  {activeTab === "receipts" && (
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered">
                        <thead>
                          <tr style={{ backgroundColor: "#81888c", color: "white" }}>
                            <th>#</th>
                            <th>Date</th>
                            <th>Description</th>
                            <th>Payment Method</th>
                            <th>Reference No.</th>
                            <th className="text-end">Amount</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {receipts.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center text-muted py-4">
                                No payment receipts found.
                              </td>
                            </tr>
                          ) : (
                            receipts.map((r, idx) => (
                              <tr key={`${r.bill_type}-${r.id}`}>
                                <td>{idx + 1}</td>
                                <td>{r.collected_date}</td>
                                <td>{r.description || `Payment for Bill No: ${r.billnum}`}</td>
                                <td>
                                  <span className="badge bg-info text-dark">
                                    {r.payment_method}
                                  </span>
                                </td>
                                <td>{r.reference_no || "—"}</td>
                                <td className="text-end text-success">
                                  ₹{Number(r.amount || 0).toFixed(2)}
                                </td>
                                <td className="text-center">
                                  <div className="btn-group btn-group-sm">
                                    <button
                                      className="btn btn-light"
                                      title="View Report"
                                      onClick={() => goToReportFromReceipt(r)}
                                    >
                                      <i className="fa fa-file-medical text-primary" />
                                    </button>
                                    <button
                                      className="btn btn-light"
                                      title="View A4 Receipt"
                                      onClick={() => goToA4Receipt(r.bill_id, r.bill_type)}
                                    >
                                      <i className="fa fa-file-alt" />
                                    </button>
                                    <button
                                      className="btn btn-light"
                                      title="View POS Receipt"
                                      onClick={() => goToPosReceipt(r.bill_id, r.bill_type)}
                                    >
                                      <i className="fa fa-print" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                        {receipts.length > 0 && (
                          <tfoot>
                            <tr className="table-secondary fw-bold">
                              <td colSpan={5} className="text-end">Total Paid</td>
                              <td className="text-end text-success">
                                ₹{receipts
                                  .reduce((s, r) => s + Number(r.amount || 0), 0)
                                  .toFixed(2)}
                              </td>
                              <td />
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PatientLog;